/*
* =======================================================================
* FILE: CaregiverObservationPage2.jsx
* -----------------------------------------------------------------------
* Displays the SECOND page of the caregiver's observation feedback.
* Updated with PDF-compatible selection styles (inline borders instead of rings/shadows)
* =======================================================================
*/

import YesIcon from '../../../../Assets/Prepratory/picsvg_download (17).svg';
import SometimesIcon from '../../../../Assets/Prepratory/picsvg_download (18).svg';
import NoIcon from '../../../../Assets/Prepratory/picsvg_download (19).svg';
import NotSureIcon from '../../../../Assets/Prepratory/picsvg_download (20).svg';
import React from "react";

// --- Fixed pixel sizes for PDF compatibility ---
const ICON_SIZE = 56;
const ICON_CONTAINER_SIZE = 70;

const responseIcons = [
    { img: YesIcon, alt: "Yes", color: "#5DB141" },
    { img: SometimesIcon, alt: "Sometimes", color: "#F7B42C" },
    { img: NoIcon, alt: "No", color: "#F2555B" },
    { img: NotSureIcon, alt: "Not sure", color: "#C9A145" },
];

const questionsForPage2 = [
    { key: 6, text: "My child can talk about how he/she feels, e.g., happy, upset, or angry." },
    { key: 7, text: "My child can calm himself/herself down during difficult situations." },
    { key: 8, text: "My child can understand how his/her friends feel." },
    { key: 9, text: "My child respects everyone's opinions." },
    { key: 10, text: "My child can help his/her friends make up after a fight." },
    { key: 11, text: "When someone is sad, my child can make them feel better." }
];

const responseKeys = [
    "can_talk_feelings",      // Q1
    "can_calm_down",          // Q2
    "can_understand_friends", // Q3
    "respects_opinions",      // Q4
    "helps_after_fight",      // Q5
    "comforts_when_sad"       // Q6
];

// Helper to get selection style - uses inline border for PDF compatibility
const getSelectionStyle = (isSelected) => {
    if (isSelected) {
        return {
            border: '3px solid #3B82F6',
            backgroundColor: '#DBEAFE',
            borderRadius: '50%',
            boxSizing: 'border-box'
        };
    }
    return {
        border: '2px solid transparent',
        borderRadius: '50%',
        boxSizing: 'border-box'
    };
};

// Helper to get checkbox style for PDF compatibility
const getCheckboxStyle = (isChecked) => {
    return {
        width: '16px',
        height: '16px',
        border: '2px solid #3B82F6',
        borderRadius: '3px',
        marginRight: '8px',
        backgroundColor: isChecked ? '#3B82F6' : 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    };
};

export const CaregiverObservationPage2 = ({ assignment }) => {
    if (!assignment || !assignment.caregiver_observation) {
        return <div className="report-page bg-white flex items-center justify-center font-[poppins]"><p>Caregiver
            Observation data not available.</p></div>;
    }

    const { assessment = {}, needs_support: support = [], other_support = '' } = assignment.caregiver_observation;

    return (
        <div className="report-page bg-white font-[poppins]" style={{
            width: '210mm',
            height: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.5',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            <table className="w-full border border-[#e36c09] border-collapse bg-white">
                <tbody>
                    {questionsForPage2.map((q, i) => (
                        <tr key={q.key} className="border-b border-[#e36c09]">
                            <td className="bg-[#FAE6D6] align-top px-3 py-2 w-[43%] border-r border-[#e36c09]">
                                <span className="text-[16px] font-normal">{q.key}. {q.text}</span>
                            </td>
                            <td className="bg-white align-top w-[57%] p-0">
                                {/* Icons Row with highlight - using inline styles for PDF */}
                                <div className="flex flex-row w-full justify-between items-center pt-1 pb-1 px-1">
                                    {responseIcons.map(({ img, alt }, idx) => {
                                        const isSelected = assessment[responseKeys[i]] === alt;
                                        return (
                                            <div
                                                key={alt}
                                                style={{
                                                    width: ICON_CONTAINER_SIZE,
                                                    height: ICON_CONTAINER_SIZE,
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    margin: '0 auto',
                                                    padding: '4px',
                                                    ...getSelectionStyle(isSelected)
                                                }}
                                            >
                                                <img
                                                    src={img}
                                                    alt={alt}
                                                    style={{ width: ICON_SIZE, height: ICON_SIZE }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Option labels row (unchanged) */}
                                <div className="flex flex-row w-full">
                                    <div className="flex-1 text-center py-[11px] text-[14px]  text-white" style={{
                                        background: '#e36c09',
                                        borderRight: "1px solid #fff"
                                    }}>Yes
                                    </div>
                                    <div className="flex-1 text-center py-[11px] text-[14px]  text-white" style={{
                                        background: '#e36c09',
                                        borderRight: "1px solid #fff"
                                    }}>Sometimes
                                    </div>
                                    <div className="flex-1 text-center py-[11px] text-[14px]  text-white" style={{
                                        background: '#e36c09',
                                        borderRight: "1px solid #fff"
                                    }}>No
                                    </div>
                                    <div className="flex-1 text-center py-[11px] text-[14px]  text-white"
                                        style={{ background: '#e36c09' }}>Not sure
                                    </div>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <table className="w-full border border-[#e36c09] border-collapse bg-white my-8"
                style={{ fontFamily: "'Times New Roman', Times, serif" }}>
                <thead>
                    <tr>
                        <th colSpan={2}
                            className="bg-[#e36c09] text-white text-center py-2 px-4 text-[18px] font-bold border-b border-[#e36c09]">
                            My child needs support with...
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {/* First row */}
                    <tr>
                        <td className="border border-[#e36c09] px-3 py-2 align-top">
                            <div className="flex items-center">
                                <div style={getCheckboxStyle(support.includes("Oral communication (R1 or R2)"))}>
                                    {support.includes("Oral communication (R1 or R2)") && (
                                        <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                                    )}
                                </div>
                                Oral communication (R1 or R2)
                            </div>
                        </td>
                        <td className="border border-[#e36c09] px-3 py-2 align-top">
                            <div className="flex items-center">
                                <div style={getCheckboxStyle(support.includes("Working with other children"))}>
                                    {support.includes("Working with other children") && (
                                        <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                                    )}
                                </div>
                                Working with other children
                            </div>
                        </td>
                    </tr>
                    {/* Second row */}
                    <tr>
                        <td className="border border-[#e36c09] px-3 py-2 align-top">
                            <div className="flex items-center">
                                <div style={getCheckboxStyle(support.includes("Reading"))}>
                                    {support.includes("Reading") && (
                                        <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                                    )}
                                </div>
                                Reading
                            </div>
                        </td>
                        <td className="border border-[#e36c09] px-3 py-2 align-top">
                            <div className="flex items-center">
                                <div style={getCheckboxStyle(support.includes("Working independently at home"))}>
                                    {support.includes("Working independently at home") && (
                                        <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                                    )}
                                </div>
                                Working independently at home
                            </div>
                        </td>
                    </tr>
                    {/* Third row */}
                    <tr>
                        <td className="border border-[#e36c09] px-3 py-2 align-top">
                            <div className="flex items-center">
                                <div style={getCheckboxStyle(support.includes("Numbers and Math"))}>
                                    {support.includes("Numbers and Math") && (
                                        <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                                    )}
                                </div>
                                Numbers and Math
                            </div>
                        </td>
                        <td className="border border-[#e36c09] px-3 py-2 align-top">
                            <div className="flex items-center">
                                <div style={getCheckboxStyle(support.includes("Other subject areas"))}>
                                    {support.includes("Other subject areas") && (
                                        <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                                    )}
                                </div>
                                <span>Other subject areas
                                    <span className="ml-2">Specify: <span
                                        className="border-b border-[#333] inline-block min-w-[140px]">{other_support || "\u00A0".repeat(15)}</span></span>
                                </span>
                            </div>
                        </td>
                    </tr>
                    {/* Fourth row */}
                    <tr>
                        <td className="border border-[#e36c09] px-3 py-2 align-top">
                            <div className="flex items-center">
                                <div style={getCheckboxStyle(support.includes("Self-confidence"))}>
                                    {support.includes("Self-confidence") && (
                                        <span style={{ color: 'white', fontSize: '12px' }}>✓</span>
                                    )}
                                </div>
                                Self-confidence
                            </div>
                        </td>
                        <td className="border border-[#e36c09] px-3 py-2 align-top"></td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};